import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { apiRequest } from "shared/api/apiRequest";
import {
  getSocietyProfileAccountingInvoiceIdContract,
  postSocietyProfileAccountingInvoiceIdContract,
} from "shared/api/contracts/society/societyId/profiles/profileId/accounting/invoice/invoiceId";
import { type SocietyProfileInvoiceBodyInput } from "shared/api/types/society/[societyId]/profiles/[profileId]/accounting/invoice";
import Spinner from "shared/components/common/Spinner";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { useSocietySession } from "shared/context/SocietySessionContext";
import { type InvoiceItemView } from "shared/mappers/database/accounting/invoice/invoice-item";
import { INVOICE_STATUS } from "hub/src/appConstants";
import PageComponent from "hub/src/components/common/PageComponent";
import {
  HubCheckoutContext,
  type HubCheckoutPageRouteParams,
} from "hub/src/pages/checkout/context/HubCheckoutContext";
import { HubCheckoutContent } from "hub/src/pages/checkout/HubCheckoutContent";
import { HubCheckoutUpdateProfileRequired } from "hub/src/pages/checkout/HubCheckoutUpdateProfileRequired";
import HubCheckoutPaymentForm from "hub/src/pages/checkout/payment-form/HubCheckoutPaymentForm";
import { PaymentType } from "hub/src/pages/checkout/payment-form/HubCheckoutPaymentFormHelpers";
import { HubCheckoutSummary } from "hub/src/pages/checkout/summary/HubCheckoutSummary";
import { type HubRouteConfig } from "hub/src/routes/RouteConfigWrapper";

export type HubCheckoutPaymentFormValues = Partial<{
  billingAddressData: {
    line1: string;
    city: string;
    state: string;
    country: string;
    postalCode?: string;
  };
  shippingAddressData: {
    line1: string;
    city: string;
    state: string;
    country: string;
    postalCode?: string;
  };
  fullName: string;
  isSameAsBilling: boolean;
  cardNumber: number;
  expirationDate: string;
  cvc: number;
  zip: number;
}>;

const CheckoutPage = (
  routeConfig: HubRouteConfig<HubCheckoutPageRouteParams>,
) => {
  const session = useCustomSession();
  const societySession = useSocietySession();

  const invoiceId = routeConfig.routeParams?.invoiceId;
  const [checkoutError, setCheckoutError] = useState<string | undefined>("");
  const [invoiceIsLoading, setInvoiceIsLoading] = useState<boolean>(false);
  // Default payment type is CARD, can be changed to other types like PAYPAL, etc.
  const [paymentType, setPaymentType] = useState(PaymentType.CARD);
  const [paymentFormValues, setPaymentFormValues] =
    useState<HubCheckoutPaymentFormValues>({});

  const [editingInvoiceItem, setEditingInvoiceItem] =
    useState<InvoiceItemView | null>(null);

  const optionalProductZone = useMemo(() => {
    return routeConfig.contentPage?.find(
      (contentZone) => contentZone.zone === "main",
    );
  }, [routeConfig.contentPage]);

  const invoice = useQuery(
    [`invoiceId-${invoiceId}`],
    async () => {
      return await apiRequest({
        contract: getSocietyProfileAccountingInvoiceIdContract,
        inputs: {
          params: {
            societyId: session.societyId!,
            profileId: session.profileId!,
            invoiceId: Number(invoiceId),
          },
        },
      });
    },
    {
      refetchOnWindowFocus: false,
      onSuccess: () => {
        setInvoiceIsLoading(false);
      },
    },
  );

  const handleUpdateInvoice = async (body: SocietyProfileInvoiceBodyInput) => {
    setInvoiceIsLoading(true);
    try {
      await apiRequest({
        contract: postSocietyProfileAccountingInvoiceIdContract,
        inputs: {
          params: {
            societyId: societySession.societyId,
            profileId: session.profileId!,
            invoiceId: invoiceId!,
          },
          body,
        },
      });
      await invoice.refetch();
    } catch (error) {
      setInvoiceIsLoading(false);
      throw error;
    }
  };

  if (!session || !invoiceId || invoice.isLoading) {
    return <Spinner />;
  }

  const payButtonDisabled =
    invoice.data?.status === INVOICE_STATUS.PAID ||
    (invoice.data?.total ?? 0) === 0 ||
    editingInvoiceItem !== null;

  return (
    <PageComponent>
      <PageComponent.Header>Payment</PageComponent.Header>
      <PageComponent.Content>
        <HubCheckoutContext.Provider
          value={{
            invoice: invoice.data,
            invoiceIsLoading,
            setInvoiceIsLoading,
            handleUpdateInvoice,
            routeConfig,
            optionalProductZone: optionalProductZone!,
            paymentType,
            setPaymentType,
            hubPaymentTypes:
              session.society!.societySettingsPublic!.hubPaymentTypes!,
            payButtonDisabled,
            checkoutError,
            setCheckoutError,
            paymentFormValues,
            editingInvoiceItem,
            setEditingInvoiceItem,
            setPaymentFormValues,
            refetchInvoice: async () => {
              await invoice.refetch();
            },
          }}
        >
          <div className="flex flex-col md:bg-neutral-light">
            <div className="w-full">
              <HubCheckoutContent />
            </div>
            <div className="flex md:flex-col">
              <div className="w-1/3 pr-6 md:w-full md:px-2">
                <HubCheckoutSummary />
              </div>

              <div className="w-2/3 pr-6 md:w-full md:p-4 -mt-4">
                <HubCheckoutPaymentForm />
              </div>
            </div>
          </div>

          <HubCheckoutUpdateProfileRequired />
        </HubCheckoutContext.Provider>
      </PageComponent.Content>
    </PageComponent>
  );
};

export default CheckoutPage;
